import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingButton } from '@mui/lab';
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { ndcRegex } from 'common/constants/regex';
import { validateValueWithRegex } from 'common/forms/formValidations';
import TextField from 'common/forms/TextField';
import { forEach } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Form } from 'react-final-form';

const validateQtyGtZero = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const num = Number(value);
  if (Number.isNaN(num)) return 'Must be a number';
  if (num <= 0) return 'Must be greater than 0';
  return undefined;
};

const arrayMutators = {
  push: ([name, value], state, { changeValue }) => {
    changeValue(state, name, (arr) => [...(arr || []), value]);
  },
  remove: ([name, index], state, { changeValue }) => {
    changeValue(state, name, (arr) => (arr || []).filter((_, i) => i !== index));
  },
};

const makeDuplicateNdcValidator = (entries, currentIndex) => (value) => {
  if (!value) return undefined;
  const isDuplicate = (entries || []).some(
    (entry, i) => i !== currentIndex && entry.ndcCode?.trim() === value.trim()
  );
  return isDuplicate ? 'Duplicate NDC' : undefined;
};

const ndcValidations = [validateValueWithRegex('Must be valid NDC format', ndcRegex)];

const BulkUpdateNDCButton = ({ gridRef }) => {
  const [numberOfRowSelected, setNumberOfRowSelected] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedNdcs, setSelectedNdcs] = useState([]);

  const updateNdc = async ({ entries }) => {
    const rows = gridRef.current.api.getSelectedNodes();
    const overrides = entries.map(({ ndcCode, quantity }) => ({ ndcCode, quantity }));

    await forEach(rows, async (row) => {
      // eslint-disable-next-line no-param-reassign
      row.data.ndcUpdateSubmitted = false;
      await gridRef.current.api.startEditingCell({
        rowIndex: row.rowIndex,
        colKey: 'overRides',
      });
      row.setDataValue('overRides', overrides);
    });

    await gridRef.current.api.stopEditing();
    setOpen(false);
  };

  const showUpdateNdcDialog = () => {
    const rows = gridRef.current.api.getSelectedNodes();
    const seen = new Set();
    setSelectedNdcs(
      rows
        .map((row) => ({ ndc: row.data?.originalNdc, qty: row.data?.quantity }))
        .filter(({ ndc, qty }) => {
          const key = `${ndc}|${qty}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
    );
    setOpen(true);
  };
  const closeUpdateNdcDialog = () => setOpen(false);

  useEffect(() => {
    const timer = setInterval(() => {
      if (gridRef.current) {
        setNumberOfRowSelected(gridRef.current.api.getSelectedRows().length);
      }
    }, 500);
    return () => clearInterval(timer);
  }, [gridRef]);

  return (
    <>
      <Button
        variant='outlined'
        size='large'
        sx={{ width: 150 }}
        disabled={!numberOfRowSelected}
        onClick={showUpdateNdcDialog}
      >
        Update NDC
        <Badge
          color='primary'
          badgeContent={numberOfRowSelected}
          max={9999}
          sx={{ ml: 2 }}
          showZero
        />
      </Button>
      <Dialog open={open} maxWidth='sm' fullWidth>
        <DialogTitle>Update NDC On Selected Rows</DialogTitle>

        <Form
          key={open ? 'open' : 'closed'}
          initialValues={{ entries: [{ ndcCode: '', quantity: '' }] }}
          onSubmit={updateNdc}
          mutators={arrayMutators}
          render={({ handleSubmit, form, values, invalid, pristine, submitting }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                  Original NDC(s)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mb: 1 }}>
                  {selectedNdcs.map((item, i) => (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: 'grey.100',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300',
                      }}
                    >
                      <Typography
                        sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.85rem' }}
                      >
                        {item.ndc}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Qty: <strong>{item.qty}</strong>
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant='subtitle2' sx={{ mb: 1.5 }}>
                  Updated NDC(s)
                </Typography>

                {(values.entries || []).map((_, index) => (
                  <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}
                  >
                    <Box sx={{ flex: 2 }}>
                      <TextField
                        name={`entries[${index}].ndcCode`}
                        id={`bulk-entries-${index}-ndcCode`}
                        label='NDC'
                        required
                        validations={[
                          ...ndcValidations,
                          makeDuplicateNdcValidator(values.entries, index),
                        ]}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        name={`entries[${index}].quantity`}
                        id={`bulk-entries-${index}-quantity`}
                        label='Qty'
                        required
                        validations={[validateQtyGtZero]}
                      />
                    </Box>
                    {values.entries.length > 1 && (
                      <IconButton
                        size='small'
                        onClick={() => form.mutators.remove('entries', index)}
                        color='error'
                        sx={{ mt: 1.5 }}
                      >
                        <DeleteIcon fontSize='large' />
                      </IconButton>
                    )}
                  </Box>
                ))}

                <Button
                  startIcon={<AddIcon />}
                  size='small'
                  onClick={() => form.mutators.push('entries', { ndcCode: '', quantity: '' })}
                  sx={{ mt: 0.5 }}
                >
                  Add More NDC
                </Button>
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' onClick={closeUpdateNdcDialog}>
                  Cancel
                </Button>
                <LoadingButton
                  variant='contained'
                  type='submit'
                  disabled={invalid || pristine || submitting}
                  loading={submitting}
                >
                  Submit
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

BulkUpdateNDCButton.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  gridRef: PropTypes.any.isRequired, // Not our function
};

export default BulkUpdateNDCButton;
